(() => {
/**
 * Accessibility focus utilities for overlays/modals.
 * Exposed on window for use by theme.js, global.js, pickup-availability.js, etc.
 */
const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** 元素是否被隐藏（自身或任意祖先 aria-hidden="true"，或 CSS 隐藏） */
function isFocusableVisible(el) {
  if (el.disabled || el.tabIndex < 0) return false;
  if (el.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  /* Per-element display/visibility is not enough: visibility is inherited but an element inside a
     visibility:hidden ancestor still reports client rects, and any descendant that re-declares
     visibility:visible passes the naive test. That let off-canvas mobile-drawer submenu panels
     count as focusable, so the drawer's initial focus landed in a panel parked at translateX(100%)
     and the browser scrolled the overflow:hidden drawer one panel width — the menu rendered blank.
     checkVisibility walks the ancestor chain (also covers content-visibility) - Plaud Tayler - 2026-08-14 17:10 */
  if (typeof el.checkVisibility === 'function') {
    return el.checkVisibility({ checkVisibilityCSS: true, contentVisibilityAuto: true });
  }
  const style = window.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null && el.getClientRects().length > 0;
}

function getFocusableElements(container) {
  if (!container || !container.querySelectorAll) return [];
  const nodes = container.querySelectorAll(FOCUSABLE_SELECTOR);
  return Array.prototype.filter.call(nodes, isFocusableVisible);
}

function trapFocus(container) {
  return function (e) {
    if (e.key !== 'Tab') return;
    if (!container || !container.contains(document.activeElement)) {
      e.preventDefault();
      moveFocusToFirst(container);
      return;
    }
    const focusable = getFocusableElements(container);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };
}

function moveFocusToFirst(container) {
  if (!container) return;
  const focusable = getFocusableElements(container);
  if (focusable.length > 0) {
    focusable[0].focus();
  } else {
    const focusTarget = container.querySelector('[tabindex="-1"]') || container;
    if (!focusTarget.hasAttribute('tabindex')) focusTarget.setAttribute('tabindex', '-1');
    if (typeof focusTarget.focus === 'function') focusTarget.focus();
  }
}

function returnFocus(opener) {
  if (opener && typeof opener.focus === 'function') {
    try {
      opener.focus();
    } catch (err) {
      // ignore
    }
  }
}

window.A11yFocusUtils = {
  getFocusableElements,
  trapFocus,
  moveFocusToFirst,
  returnFocus
};

/**
 * Keep vendor bundles on the test baseline while applying the dialog semantics
 * required by dynamically-created Tingle modals.
 */
(function initTingleModalA11y() {
  function normalizeModal(modal) {
    const modalBox = modal.querySelector('.tingle-modal-box');
    if (modalBox) {
      modalBox.setAttribute('role', 'dialog');
      modalBox.setAttribute('aria-modal', 'true');
      if (!modalBox.hasAttribute('tabindex')) modalBox.setAttribute('tabindex', '-1');
    }

    const closeButton = modal.querySelector('.tingle-modal__close');
    if (closeButton && !closeButton.hasAttribute('aria-label')) {
      const visibleLabel = closeButton.querySelector('.tingle-modal__closeLabel')?.textContent.trim();
      closeButton.setAttribute(
        'aria-label',
        visibleLabel || window.accessibilityStrings?.close || 'Close'
      );
    }
  }

  function normalizeWithin(rootNode) {
    if (!(rootNode instanceof Element)) return;
    if (rootNode.matches('.tingle-modal')) normalizeModal(rootNode);
    rootNode.querySelectorAll('.tingle-modal').forEach(normalizeModal);
  }

  function start() {
    document.querySelectorAll('.tingle-modal').forEach(normalizeModal);
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(normalizeWithin);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/** Popup-addons: 当弹框仅通过 focus/hover 显示（未走 tingle 模态）时，把焦点限制在弹框内并关闭时恢复焦点。必须绑在 document 上才能拦截到「焦点在弹框外」时的 Tab。 */
function setupPopupAddonsFocusTrap(addon, content, moveFocusInto) {
  if (!content || !window.A11yFocusUtils) return;
  if (content.closest('.tingle-modal')) return; // 已由 global.js 的模态做 focus trap
  if (addon._a11yPopupAddonsTrapHandler) return; // 已设置过
  const opener = document.activeElement;
  addon._a11yPopupAddonsOpener = opener;
  addon._a11yPopupAddonsContent = content;
  if (!content.hasAttribute('tabindex')) content.setAttribute('tabindex', '-1');
  const handler = window.A11yFocusUtils.trapFocus(content);
  addon._a11yPopupAddonsTrapHandler = handler;
  document.addEventListener('keydown', handler, true);
  if (moveFocusInto) window.A11yFocusUtils.moveFocusToFirst(content);
}

function teardownPopupAddonsFocusTrap(addon) {
  if (!addon || !addon._a11yPopupAddonsTrapHandler) return;
  document.removeEventListener('keydown', addon._a11yPopupAddonsTrapHandler, true);
  if (window.A11yFocusUtils && addon._a11yPopupAddonsOpener) {
    window.A11yFocusUtils.returnFocus(addon._a11yPopupAddonsOpener);
  }
  addon._a11yPopupAddonsTrapHandler = null;
  addon._a11yPopupAddonsContent = null;
  addon._a11yPopupAddonsOpener = null;
}

document.addEventListener('DOMContentLoaded', () => {
  // WCAG 1.4.13: Content on Hover or Focus
  // Requirement: Dismissible, Hoverable, Persistent

  // A tooltip is the topmost transient layer. Handle Escape in capture phase
  // so an enclosing dialog does not close at the same time.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const focusedTooltip = document.activeElement?.closest('.tooltip:not(.tooltip-dismissed)');
    const hoveredTooltip = Array.from(document.querySelectorAll('.tooltip')).find((tooltip) =>
      !tooltip.classList.contains('tooltip-dismissed') &&
      (tooltip.matches(':hover') || tooltip.classList.contains('open'))
    );
    const tooltip = focusedTooltip || hoveredTooltip;
    if (!tooltip) return;
    const content = tooltip.querySelector('.tooltip-content');
    if (content) content.setAttribute('aria-hidden', 'true');
    tooltip.classList.add('tooltip-dismissed');
    tooltip.classList.remove('open');
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  /**
   * Helper to handle safe dismissal (ESC key)
   */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const finishDismissal = () => {
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    // The dedicated capture listener above normally handles tooltips. Keep a
    // focused-tooltip fallback here for dynamically replaced content.
    const activeTooltip = document.activeElement?.closest('.tooltip:not(.tooltip-dismissed)');
    if (activeTooltip) {
      const content = activeTooltip.querySelector('.tooltip-content');
      if (content) content.setAttribute('aria-hidden', 'true');
      activeTooltip.classList.add('tooltip-dismissed');
      activeTooltip.classList.remove('open');
      finishDismissal();
      return;
    }

    // Search owns its focus cleanup, animation and opener restoration in
    // theme.js. While it is open (including its closing animation), do not let
    // a stale background layer consume the same Escape key.
    if (document.documentElement.classList.contains('open-search')) return;

    // Scope transient controls to the topmost visible modal so a stale header
    // menu or dropdown behind it cannot consume Escape.
    const dialogs = Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]')).filter((candidate) => {
      if (candidate.closest('#search-form, .popup-search, .header_search, .search-popup, #search-drawer')) return false;
      if (candidate.closest('[aria-hidden="true"], [inert], [hidden]')) return false;
      if (candidate.classList.contains('hidden')) return false;
      const style = window.getComputedStyle(candidate);
      return style.display !== 'none' && style.visibility !== 'hidden' && candidate.getClientRects().length > 0;
    });
    const dialog = dialogs[dialogs.length - 1];
    const layerRoot = dialog || document;

    // Desktop header menus are owned by BlsMainMenuShopify in theme.js. Do not
    // mutate them here: its bubbling Escape listener also clears animation,
    // header and isActive state, then restores focus to the owning link.
    // Close only the active/topmost non-header menu layer in the current UI layer.
    const menuSelector = '.dropdown-menu.visible, .mega-menu.visible, .menu-parent.visible, .submenu.visible';
    const visibleMenus = Array.from(layerRoot.querySelectorAll(menuSelector)).filter((candidate) =>
      !candidate.matches('.menu-parent__horizontal') &&
      !candidate.closest('.menu-parent__horizontal')
    );
    const focusedMenu = document.activeElement?.closest(menuSelector);
    const menu = focusedMenu && layerRoot.contains(focusedMenu) && visibleMenus.includes(focusedMenu)
      ? focusedMenu
      : visibleMenus[visibleMenus.length - 1];
    if (menu) {
      menu.classList.remove('visible', 'open');
      if (menu.parentElement) {
        menu.parentElement.classList.remove('visible', 'open');
        const trigger = menu.parentElement.querySelector('[aria-expanded]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
      finishDismissal();
      return;
    }

    // Sort dropdown（须走 SelectSorter.close，否则 .active / 监听与 aria-hidden 不同步）
    const openSorters = Array.from(layerRoot.querySelectorAll('select-sorter[aria-expanded="true"]'));
    const focusedSorter = document.activeElement?.closest('select-sorter[aria-expanded="true"]');
    const sorter = focusedSorter && layerRoot.contains(focusedSorter)
      ? focusedSorter
      : openSorters[openSorters.length - 1];
    if (sorter) {
      if (typeof sorter.close === 'function') {
        sorter.close();
      } else {
        sorter.setAttribute('aria-expanded', 'false');
        sorter.closest('.select-custom')?.classList.remove('active');
        const panelId = sorter.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (panel) panel.setAttribute('aria-hidden', 'true');
      }
      requestAnimationFrame(() => requestAnimationFrame(() => sorter.focus()));
      finishDismissal();
      return;
    }

    // Account dropdown (theme uses is-open).
    const accountWrapper = layerRoot.querySelector('[data-account-dropdown-wrapper]');
    if (accountWrapper) {
      const trigger = accountWrapper.querySelector('[data-account-dropdown-trigger]');
      const accountMenu = accountWrapper.querySelector('[data-account-dropdown-menu]');
      if (accountMenu && (accountMenu.classList.contains('is-open') || trigger?.getAttribute('aria-expanded') === 'true')) {
        accountMenu.hidden = true;
        accountMenu.classList.remove('is-open');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (window.A11yFocusUtils && accountWrapper._a11yOpener) {
          window.A11yFocusUtils.returnFocus(accountWrapper._a11yOpener);
        }
        finishDismissal();
        return;
      }
    }

    // Popup addons that are not already inside a tingle dialog.
    const openAddons = Array.from(layerRoot.querySelectorAll('popup-addons')).filter((addon) => {
      const content = addon.querySelector('.popup-addons__content');
      return content && !content.classList.contains('hidden') && !content.closest('.tingle-modal');
    });
    const addon = openAddons[openAddons.length - 1];
    if (addon) {
      teardownPopupAddonsFocusTrap(addon);
      addon.querySelector('.popup-addons__content')?.classList.add('hidden');
      finishDismissal();
      return;
    }

    // Close only the topmost visible non-search modal.
    const closeBtn = dialog?.querySelector(
      '.close, .close-cart-button, .popup-close-btn, .close-before, .tingle-modal__close'
    );
    if (closeBtn) {
      closeBtn.click();
      finishDismissal();
      return;
    }
    // Some dialog implementations (for example tingle) keep their close
    // control outside the role=dialog node and own Escape themselves. Do not
    // dismiss a background layer while that handler is about to run.
    if (dialog) return;

    const stickyBar = document.querySelector('#sticky-bar.open');
    if (stickyBar) {
      stickyBar.classList.remove('open');
      finishDismissal();
    }
  }, true);

  /**
   * Event Delegation for Tooltips, Popups, etc.
   * Handles dynamic content better.
   */

  // Focus In
  document.addEventListener('focusin', (e) => {
      // Tooltips
      const tooltip = e.target.closest('.tooltip');
      if (tooltip) {
          const content = tooltip.querySelector('.tooltip-content');
          if (content) {
              if (!content.id) {
                  content.id = `tooltip-content-${Math.random().toString(36).substr(2, 9)}`;
                  tooltip.setAttribute('aria-describedby', content.id);
              }
              content.setAttribute('role', 'tooltip');
              content.removeAttribute('aria-hidden');
          }
          tooltip.classList.remove('tooltip-dismissed');
          tooltip.classList.add('open');
      }

      // popup-addons 通过 click/Enter 触发 tingle 弹框，无需 focus 时改变显示状态
      // focusTrap 在 PopupBase.initPopup 打开弹框时由 tingle 自身接管

      // Sort Dropdown: aria-expanded is managed by SelectSorter component
  });

  // Focus Out
  document.addEventListener('focusout', (e) => {
      // Tooltips
      const tooltip = e.target.closest('.tooltip');
      if (tooltip) {
          if (!tooltip.contains(e.relatedTarget)) {
              const content = tooltip.querySelector('.tooltip-content');
              if (content) content.setAttribute('aria-hidden', 'true');
              tooltip.classList.remove('open');
          }
      }

      // popup-addons 由 tingle 管理焦点，此处不干预
  });

  // Mouse Over
  document.addEventListener('mouseover', (e) => {
      // Tooltips
      const tooltip = e.target.closest('.tooltip');
      if (tooltip && !tooltip.contains(e.relatedTarget)) {
          tooltip.classList.remove('tooltip-dismissed');
      }
      // popup-addons 通过 click 触发 tingle 弹框，不使用 hover 显示逻辑
  });

  // Click Outside (for Sort Dropdown)
  document.addEventListener('click', (e) => {
       const sorter = e.target.closest('select-sorter');
       document.querySelectorAll('select-sorter[aria-expanded="true"]').forEach((s) => {
           if (s !== sorter && typeof s.close === 'function') {
               s.close();
           }
       });
  });

  /**
   * Search Popup Initialization
   */
   const searchTriggers = document.querySelectorAll('.header-search-popup, .header_search, .btn-search');
   searchTriggers.forEach(trigger => {
       if (!trigger.hasAttribute('aria-expanded')) {
           trigger.setAttribute('aria-expanded', 'false');
       }
       trigger.addEventListener('click', () => {
           setTimeout(() => {
               const isOpen = document.documentElement.classList.contains('open-search') || trigger.classList.contains('open');
               trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
           }, 50);
       });
   });

  /**
   * Recommendation Popup Observer: trap focus when shown, return focus and remove trap when hidden.
   */
    const recommendationPopups = document.querySelectorAll('.recommendation-popup');
    recommendationPopups.forEach(popup => {
        if (popup.closest('ai-plan-recommendation')) return;
        let wasVisible = popup.style.display !== 'none' && !popup.classList.contains('hidden');
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'style' || mutation.attributeName === 'class') {
                    const isVisible = popup.style.display !== 'none' && !popup.classList.contains('hidden');
                    if (isVisible && !wasVisible) {
                        popup._a11yOpener = document.activeElement;
                        moveFocusToFirst(popup);
                        if (!popup.hasAttribute('data-focus-trap')) {
                            popup.setAttribute('data-focus-trap', 'true');
                            const handler = trapFocus(popup);
                            popup._a11yTrapHandler = handler;
                            document.addEventListener('keydown', handler, true);
                        }
                    } else if (!isVisible && wasVisible) {
                        if (popup._a11yTrapHandler) {
                            document.removeEventListener('keydown', popup._a11yTrapHandler, true);
                            popup._a11yTrapHandler = null;
                        }
                        popup.removeAttribute('data-focus-trap');
                        if (window.A11yFocusUtils && popup._a11yOpener) {
                            window.A11yFocusUtils.returnFocus(popup._a11yOpener);
                        }
                        popup._a11yOpener = null;
                    }
                    wasVisible = isVisible;
                }
            });
        });
        observer.observe(popup, { attributes: true });
    });
});

/* Search clear button: show/hide with input content, clear & return focus */
(function initSearchClearButtons() {
    function setup(input) {
        if (input._clearBtnInit) return;
        input._clearBtnInit = true;

        const container = input.closest('.field') || input.parentElement;
        const clearBtn = container?.querySelector('.search__clear-btn');
        if (!clearBtn) return;

        function toggle() {
            clearBtn.hidden = !input.value.trim();
        }

        input.addEventListener('input', toggle);
        toggle();

        clearBtn.addEventListener('click', function () {
            input.value = '';
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.focus();
        });
    }

    function initAll() {
        document.querySelectorAll('input[type="search"]').forEach(setup);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAll);
    } else {
        initAll();
    }

    new MutationObserver(initAll).observe(document.body || document.documentElement, {
        childList: true,
        subtree: true
    });
})();
})();
